import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Zap, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const trustPoints = [
    { icon: MapPin, text: 'London & nationwide operations' },
    { icon: Zap, text: 'Structured, scalable workforce' },
    { icon: CheckCircle2, text: 'Multi-site contract specialists' },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 bg-white"
      data-testid="hero-section"
    >
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1
                className="font-oswald text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-tight mb-4 md:mb-6"
                data-testid="hero-headline"
              >
                <span className="text-primary">Your Facility Management </span>
                <span className="text-slate-800">Partner in </span>
                <span className="text-accent">London & the UK</span>
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <p
                className="font-manrope text-base md:text-lg text-slate-600 mb-6 md:mb-8 max-w-xl leading-relaxed"
                data-testid="hero-supporting-text"
              >
                We deliver consistent, high-standard commercial cleaning and professional staffing solutions 
                for offices, healthcare facilities, retail environments, and hospitality operations. 
                Built for long-term partnerships with structured processes, accountable teams, 
                and scalable service delivery.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <Button
                onClick={() => scrollToSection('#contact')}
                size="lg"
                className="bg-accent hover:bg-accent-hover text-white font-manrope font-bold text-base px-8 py-6 rounded-sm btn-glow"
                data-testid="hero-get-quote-btn"
              >
                Request a Proposal
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => scrollToSection('#contact')}
                variant="outline"
                size="lg"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white font-manrope font-bold text-base px-8 py-6 rounded-sm"
                data-testid="hero-callback-btn"
              >
                Schedule a Consultation
              </Button>
            </motion.div>

            {/* Trust Points */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap gap-6"
            >
              {trustPoints.map((point, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-slate-700"
                  data-testid={`trust-point-${index}`}
                >
                  <point.icon className="w-5 h-5 text-accent" />
                  <span className="font-manrope text-sm font-medium">{point.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-sm overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1758448721162-0c77cf477d6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMGxvYmJ5JTIwaW50ZXJpb3IlMjBwcm9mZXNzaW9uYWwlMjBidXNpbmVzc3xlbnwwfHx8fDE3NzM4OTgwNjl8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern commercial office lobby - professional cleaning services London"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
            </div>
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="absolute -bottom-6 -left-6 bg-primary p-6 rounded-sm shadow-xl"
            >
              <div className="font-oswald text-3xl font-bold text-white mb-1">London</div>
              <div className="font-manrope text-sm text-white/90">& UK Coverage</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="font-manrope text-xs text-slate-500 uppercase tracking-widest">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-slate-300 rounded-full flex items-start justify-center p-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-accent rounded-full" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
